import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { add } from "dinero.js";
import { cents, showMoneyShort, toCents } from "../Data/Money";
import { FacilityParkerBalance } from "../Data/ApiTypes";
import { balancesAtom } from "../Data/Atoms";
import { WalletFill } from "react-bootstrap-icons";

interface OutstandingBalanceBannerProps {
    className?: string;
    perFacility?: boolean;
}

const GRADIENT = "linear-gradient(135deg, #e8359a 0%, #c0116e 100%)";

function BannerRow( { label, amount, payUrl, className }: {
    label:     string;
    amount:    FacilityParkerBalance["balance"];
    payUrl:    string;
    className: string;
} ) {
    const nav = useNavigate();
    return (
        <div
            className={`rounded-3 d-flex align-items-center justify-content-between gap-3 px-3 py-3 ${className}`}
            style={{ background: GRADIENT, color: "#fff" }}
        >
            <div className="d-flex align-items-center gap-2">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 36, height: 36, backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                    <WalletFill size={18} />
                </div>
                <div>
                    <div className="small text-uppercase fw-semibold" style={{ opacity: 0.85, letterSpacing: "0.04em" }}>
                        Outstanding Balance
                    </div>
                    <div className="fw-bold fs-5">{showMoneyShort( amount )} due</div>
                    {label && <div className="small" style={{ opacity: 0.85 }}>{label}</div>}
                </div>
            </div>
            <button
                type="button"
                className="btn fw-semibold rounded-pill px-3 py-1 flex-shrink-0"
                style={{ backgroundColor: "#fff", color: "#a00d5a", fontSize: "0.9rem" }}
                onClick={() => nav( payUrl )}
            >
                Pay now
            </button>
        </div>
    );
}

export function OutstandingBalanceBanner( { className = "mb-3", perFacility = false }: OutstandingBalanceBannerProps ) {
    const balances = useAtomValue( balancesAtom );

    const owedBalances = useMemo( () => balances.filter( b => toCents( b.balance ) > 0 ), [balances] );
    const totalDebt    = useMemo(
        () => owedBalances.map( b => b.balance ).reduce( ( a, b ) => add( a, b ), cents( 0 ) ),
        [owedBalances]
    );

    if ( toCents( totalDebt ) <= 0 ) return null;

    if ( perFacility && owedBalances.length > 1 ) {
        return (
            <>
                {owedBalances.map( ( b, i ) => (
                    <BannerRow
                        key={b.facility.facilityId}
                        label={b.facility.name}
                        amount={b.balance}
                        payUrl={`/user/${b.facility.facilityId}/pay`}
                        className={i < owedBalances.length - 1 ? "mb-2" : className}
                    />
                ) )}
            </>
        );
    }

    const payUrl = owedBalances.length === 1
        ? `/user/${owedBalances[0].facility.facilityId}/pay`
        : "/user/order";

    return (
        <BannerRow
            label=""
            amount={totalDebt}
            payUrl={payUrl}
            className={className}
        />
    );
}
